import type { ApiClient } from 'grabbitbot-legacy-ui'
import { notifyConnectionsChanged } from './connectionEvents'

/**
 * B4 domain client — ports `V3SpotBotService`'s WRITE methods
 * (`src/app/data/api-client/v3/v3-spot-bot.service.ts` in the legacy
 * archive: `startBot`, `closePositions`, `deleteAllPositions`,
 * `openMopManually`). BUSINESS-CRITICAL: every one of these four routes
 * places, closes or deletes a real order on the user's exchange account via
 * spotbot. See legacy-migration `docs/analysis/2026-09-27-grabbitbot-b4-
 * contract.md` for the pinned contract (api.grabbitbot.com 0.5.0).
 *
 * Unlike the legacy Angular client (which sent `positions` via the query
 * string and `amount` via the JSON body), every write here uses a JSON body
 * — the contract documents both channels as accepted ("from the query
 * (wins) or, if absent there, the body"), and a body is simpler to build
 * correctly with the shared `ApiClient` than a hand-rolled indexed query
 * string. Nothing here is retried — the contract is explicit that a 502
 * (spotbot unreachable) leaves the outcome UNKNOWN and must never be
 * auto-retried (a retried buy/sell/delete is a second real order).
 */
/** A start or delete changes which connections exist, so every cached config
 * read (the default-config list the settings pages AND the header picker
 * load) must be dropped — otherwise the reload right after the request
 * shows the OLD list and the user sees "nothing happened". */
const CONFIG_CACHE = /\/v3\/spot\/config\//

export function createBotControlClient(client: ApiClient) {
  return {
    /** #1 — starts (or re-fetches the status of) the bot on `exchangeId`.
     * On 200 the answer is spotbot's bot status, plus a `defaultConfig` key
     * with the PATCH's answer — ABSENT (not `null`) when the default-config
     * PATCH itself failed, even though the bot status WAS created. Callers
     * must check `'defaultConfig' in result`, not just falsiness. */
    startBot: (exchangeId: string) =>
      client.post<Record<string, unknown>>('/v3/spot/bot/{exchangeId}', undefined, {
        params: { exchangeId },
        invalidates: [CONFIG_CACHE],
      }).then((result) => {
        notifyConnectionsChanged()
        return result
      }),
    /** Deletes one exchange CONNECTION (`exchangeId` = the exact connection
     * id, e.g. `wnfexchange_1759150000000`): its bot status, default config,
     * coin configs, tags and stored exchange key; trading history is kept.
     * 200 `{deleted: true, exchangeId}`. The backend refuses (409/404) while
     * coins are trading, a run is in progress, positions/orders are open or
     * the bot is halted. Never auto-retried — a 502 leaves the outcome
     * UNKNOWN. */
    deleteConnection: (exchangeId: string) =>
      client.delete<{ deleted: boolean; exchangeId: string }>('/v3/spot/bot/{exchangeId}', {
        params: { exchangeId },
        invalidates: [CONFIG_CACHE],
      }).then((result) => {
        notifyConnectionsChanged()
        return result
      }),
    /** #2 — force-sells and closes the given open positions. `positions` is
     * a list of position ids; an empty list is a legitimate no-op (spotbot
     * closes nothing) rather than an error. */
    closeSelectedPositions: (exchangeId: string, positions: string[]) =>
      client.post<unknown>(
        '/v3/spot/bot/{exchangeId}/positions/open/close',
        { positions },
        { params: { exchangeId } },
      ),
    /** #3 — deletes (without selling) every open position of one coin pair.
     * Only offered while the coin is in an "Off" trading mode (-1 / -100),
     * same restriction the legacy UI enforced. */
    deleteAllPositions: (exchangeId: string, coin1: string, coin2: string) =>
      client.post<unknown>('/v3/spot/bot/{exchangeId}/positions/open/{coin1}/{coin2}/deleteall', undefined, {
        params: { exchangeId, coin1, coin2 },
      }),
    /** #4 — manual buy (legacy "Open MOP manually" / LightBot). `amount`
     * MUST be the exact string the user typed — no float parsing/formatting
     * (contract: a JSON number goes through PHP's lossy `(string)` cast, a
     * string is forwarded byte-for-byte). A falsy amount answers 409
     * `no_amount` without ever reaching spotbot. */
    openPositionManually: (exchangeId: string, coin1: string, coin2: string, amount: string) =>
      client.post<unknown>(
        '/v3/spot/bot/{exchangeId}/light/{coin1}/{coin2}/position/open',
        { amount },
        { params: { exchangeId, coin1, coin2 } },
      ),
  }
}

export type BotControlClient = ReturnType<typeof createBotControlClient>
